import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const DemoVideo: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Box
      component="section"
      id="demo"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, white 0%, #F8FAFC 100%)',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Chip
              icon={<PlayCircleOutlineIcon />}
              label={t('demo.duration')}
              sx={{
                mb: 3,
                backgroundColor: 'white',
                color: '#0F172A',
                fontWeight: 600,
                border: '1px solid #E2E8F0',
                px: 1,
                '& .MuiChip-icon': { color: '#0F172A' },
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.8rem' },
                mb: 2,
                color: '#0F172A',
                letterSpacing: '-0.02em',
              }}
            >
              {t('demo.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#64748B',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {t('demo.subtitle')}
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '1000px',
              mx: 'auto',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.18)',
              border: '1px solid #E2E8F0',
              backgroundColor: 'rgb(13, 13, 37)',
              aspectRatio: '16 / 9',
            }}
          >
            <Box
              component="iframe"
              src="https://www.youtube.com/embed/V53ZWKWYVxM?rel=0"
              title={t('demo.videoTitle')}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default DemoVideo;
